import React, { useCallback, memo } from 'react';
import { Box, IconButton, Typography, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { LovDictionary } from 'src/services/lov-service';
import PredefinedVariableValueSelect from 'src/components/PredefinedVariableValueSelect';

export interface PropertyItem {
  key: string;
  value: string;
}

interface RepeatingPropertiesFieldProps {
  value: PropertyItem[];
  onChange: (properties: PropertyItem[]) => void;
  predefinedValueOptions: LovDictionary;
  disabled?: boolean;
  title?: string;
}

const PropertyRow = memo<{
  property: PropertyItem;
  index: number;
  predefinedValueOptions: LovDictionary;
  disabled: boolean;
  onUpdate: (index: number, field: 'key' | 'value', newValue: string) => void;
  onRemove: (index: number) => void;
}>(({ property, index, predefinedValueOptions, disabled, onUpdate, onRemove }) => (
  <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
    <Grid item xs={5}>
      <FormControl fullWidth size="small">
        <InputLabel>Property</InputLabel>
        <Select
          value={property.key}
          onChange={(e) => onUpdate(index, 'key', e.target.value)}
          label="Property"
          disabled={disabled}
        >
          <MenuItem value="">
            <em>Select Property</em>
          </MenuItem>
          {Object.entries(predefinedValueOptions).map(([id, name]) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={5}>
      <PredefinedVariableValueSelect
        predefinedVariableId={property.key}
        value={property.value}
        onChange={(newValue) => onUpdate(index, 'value', newValue)}
        disabled={disabled || !property.key}
        label="Value"
        size="small"
      />
    </Grid>
    <Grid item xs={2}>
      <IconButton
        onClick={() => onRemove(index)}
        size="small"
        color="error"
        disabled={disabled}
        sx={{ ml: 1 }}
      >
        <DeleteIcon />
      </IconButton>
    </Grid>
  </Grid>
));

PropertyRow.displayName = 'PropertyRow';

const RepeatingPropertiesField: React.FC<RepeatingPropertiesFieldProps> = ({
  value = [],
  onChange,
  predefinedValueOptions,
  disabled = false,
  title = 'Properties'
}) => {
  const addProperty = useCallback(() => {
    const newProperties = [...value, { key: '', value: '' }];
    onChange(newProperties);
  }, [value, onChange]);

  const removeProperty = useCallback((index: number) => {
    const newProperties = value.filter((_, i) => i !== index);
    onChange(newProperties);
  }, [value, onChange]);

  const updateProperty = useCallback((index: number, field: 'key' | 'value', newValue: string) => {
    const newProperties = value.map((prop, i) => 
      i === index ? { ...prop, [field]: newValue } : prop
    );
    onChange(newProperties);
  }, [value, onChange]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton 
          onClick={addProperty} 
          size="small" 
          color="primary"
          disabled={disabled}
        >
          <AddIcon />
        </IconButton>
      </Box>
      
      {value.map((property, index) => (
        <PropertyRow
          key={index}
          property={property}
          index={index}
          predefinedValueOptions={predefinedValueOptions}
          disabled={disabled}
          onUpdate={updateProperty}
          onRemove={removeProperty}
        />
      ))}
      
      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
          No properties added. Click the + button to add a property.
        </Typography>
      )}
    </Box>
  );
};

export default memo(RepeatingPropertiesField);